<template>
  <v-card
    elevation="3"
    rounded="lg"
  >
    <v-toolbar
      flat
      dense
      color="color_green"
      dark
    >
      <v-toolbar-title class="text-subtitle-2">
        Calls Detail
        <span
          v-if="username"
          class="text-caption ml-2"
        >({{ username }})</span>
      </v-toolbar-title>
      <v-spacer />
      <v-btn
        icon
        @click="$emit('close')"
      >
        <v-icon>
          mdi-close
        </v-icon>
      </v-btn>
    </v-toolbar>

    <!-- Date filters -->
    <v-card-text class="pb-0">
      <v-row
        dense
        align="center"
      >
        <v-col
          cols="12"
          sm="4"
        >
          <v-text-field
            v-model="dateFrom"
            label="From (YYYY-MM-DD)"
            type="date"
            dense
            outlined
            clearable
            prepend-inner-icon="mdi-calendar-start"
            hide-details
            :min="yearDateMin"
            :max="yearDateMax"
          />
        </v-col>
        <v-col
          cols="12"
          sm="4"
        >
          <v-text-field
            v-model="dateTo"
            label="To (YYYY-MM-DD)"
            type="date"
            dense
            outlined
            clearable
            prepend-inner-icon="mdi-calendar-end"
            hide-details
            :min="yearDateMin"
            :max="yearDateMax"
          />
        </v-col>
        <v-col
          cols="12"
          sm="4"
        >
          <v-btn
            color="color_green"
            dark
            depressed
            :loading="loading"
            @click="doSearch"
          >
            <v-icon left>
              mdi-magnify
            </v-icon>
            Search
          </v-btn>
          <v-btn
            class="ml-2"
            text
            small
            :disabled="!items.length"
            @click="exportCsv"
          >
            <v-icon
              left
              small
            >
              mdi-download
            </v-icon>
            CSV
          </v-btn>
        </v-col>
      </v-row>
    </v-card-text>

    <v-data-table
      :headers="headers"
      :items="items"
      :loading="loading"
      :items-per-page="20"
      dense
      class="mt-2 calls-detail-table"
    >
      <template #item.service="{ item }">
        <v-chip
          x-small
          dark
          color="color_green"
        >
          {{ item.service }}
        </v-chip>
      </template>
      <template #item.call_time="{ item }">
        <span class="text-caption">{{ item.call_time }}</span>
      </template>
      <template #item.lat="{ item }">
        <span class="text-caption">{{ formatCoord(item.lat) }}</span>
      </template>
      <template #item.lon="{ item }">
        <span class="text-caption">{{ formatCoord(item.lon) }}</span>
      </template>
      <template #item.layers="{ item }">
        <span
          class="text-caption layers-cell"
          :title="item.layers"
        >{{ item.layers || '—' }}</span>
      </template>
      <template #item.time_range="{ item }">
        <span
          class="text-caption time-range-cell"
          :title="item.time_range"
        >{{ item.time_range || '—' }}</span>
      </template>
    </v-data-table>
  </v-card>
</template>

<script>
import { clampDateRangeToYear, yearDateBounds } from '@/api/statisticsParams.js';

const CSV_COLUMNS = [
  'transaction_id',
  'service',
  'ip',
  'call_time',
  'workspace',
  'station_name',
  'lat',
  'lon',
  'height',
  'layers',
  'time_range',
  'timezone',
  'output_fmt'
];

export default {
  name: 'CallsByUserTable',
  props: {
    items: { type: Array, default: () => [] },
    username: { type: String, default: null },
    loading: { type: Boolean, default: false },
    initialDateFrom: { type: String, default: null },
    initialDateTo: { type: String, default: null },
    year: { type: Number, required: true }
  },
  data() {
    return {
      dateFrom: null,
      dateTo: null,
      headers: [
        { text: 'Transaction ID', value: 'transaction_id', sortable: false },
        { text: 'Service', value: 'service' },
        { text: 'Workspace', value: 'workspace', sortable: false },
        { text: 'Station', value: 'station_name', sortable: false },
        { text: 'Lat', value: 'lat', align: 'end', sortable: false },
        { text: 'Lon', value: 'lon', align: 'end', sortable: false },
        { text: 'H', value: 'height', align: 'end', sortable: false },
        { text: 'Layers', value: 'layers', sortable: false },
        { text: 'Time range', value: 'time_range', sortable: false },
        { text: 'TZ', value: 'timezone', sortable: false },
        { text: 'Fmt', value: 'output_fmt', sortable: false },
        { text: 'IP', value: 'ip', sortable: false },
        { text: 'Call Time', value: 'call_time' }
      ]
    };
  },
  computed: {
    yearDateMin() {
      return yearDateBounds(this.year).min;
    },
    yearDateMax() {
      return yearDateBounds(this.year).max;
    }
  },
  mounted() {
    this.dateFrom = this.initialDateFrom;
    this.dateTo = this.initialDateTo;
  },
  methods: {
    formatCoord(value) {
      if (value === null || value === undefined || value === '') {
        return '—';
      }

      return Number(value).toFixed(4);
    },
    doSearch() {
      const { date_from, date_to } = clampDateRangeToYear(
        this.dateFrom,
        this.dateTo,
        this.year
      );

      this.dateFrom = date_from;
      this.dateTo = date_to;

      this.$emit('search', {
        date_from,
        date_to
      });
    },
    exportCsv() {
      const header = CSV_COLUMNS.join(',');
      const rows = this.items.map(r =>
        CSV_COLUMNS.map(c => `"${(r[c] ?? '').toString().replace(/"/g, '""')}"`).join(',')
      );
      const blob = new Blob([[header, ...rows].join('\n')], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');

      a.href = url;
      a.download = `calls_${this.username}_${Date.now()}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    }
  }
};
</script>

<style scoped>
.calls-detail-table {
  overflow-x: auto;
}
.layers-cell,
.time-range-cell {
  display: inline-block;
  max-width: 140px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: bottom;
}
</style>
